/**
 * Utility functions for CDN URL handling
 */

/**
 * Converts a relative or full image URL to the appropriate CDN.
 * This should be used for all dynamically loaded images from the theme/config.
 *
 * Supports two types of image serving:
 * 1. Static bundled images (/images/*) - served from versioned app CDN
 * 2. Dynamic media assets (/media/*) - served from backend S3 MRAP via /api/media
 *
 * @param imageUrl - The image URL from the backend (can be relative or absolute)
 * @returns The fully resolved URL (CDN or API endpoint)
 */
export function getImageURL(imageUrl: string | undefined | null): string {
  if (!imageUrl) return '';

  // If the URL is already absolute (starts with http:// or https://), return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // For relative URLs, ensure they start with /
  const normalizedUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;

  // Get the asset base from environment (empty string when CDN is disabled)
  const assetBase = process.env.NEXT_PUBLIC_ASSET_BASE || '';

  // Route /media/{themeId}/* to backend API endpoint (S3 MRAP)
  if (normalizedUrl.startsWith('/media/')) {
    // Path format: /media/{themeId}/{hash}.{ext}
    // In development, proxy through Next.js dev server
    // In production, use the backend API directly
    return `/api${normalizedUrl}`;
  }

  // Route /images/* to versioned app CDN (existing behavior)
  return assetBase ? `${assetBase}${normalizedUrl}` : normalizedUrl;
}
